/**
 * usePollingResource (pirx-cvwv2.1)
 *
 * Shared poll-with-timeout/stop-on-403 state machine. Extracted from
 * usePipelineTransparencyPolling, usePipelineRunEventsPolling, and
 * usePipelineRunCostPolling, which were ~90% line-for-line copies of this
 * same state machine with only the fetch call and result type swapped.
 *
 * Behavior (identical to what the three callers already had):
 * - Fetches once when `key` becomes defined, and again whenever `key`
 *   changes (resetting data/error state and the poll-duration clock first).
 * - Re-fetches every `opts.intervalMs` while polling.
 * - Stops polling automatically once `opts.maxDurationMs` has elapsed since
 *   the last (re)start, or immediately on an `ApiError` whose status is in
 *   `opts.stopOnStatuses` (default `[403]` — the caller isn't allowlisted for
 *   this resource; retrying won't help), or when `opts.isTerminal?.(data)`
 *   returns true on a successful fetch.
 * - Exposes `refresh()` to manually restart polling (resets the duration
 *   clock and polling flag, then fetches immediately).
 *
 * `fetchFn` does not need to be memoized by the caller — it is read from a
 * ref on every call, so passing a fresh closure each render is fine. Only
 * `key` and `opts` drive when effects re-run.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/services/api-client";

export interface UsePollingResourceOptions<T = unknown> {
  intervalMs: number;
  maxDurationMs: number;
  isTerminal?: (data: T) => boolean;
  /**
   * `ApiError` statuses that permanently stop polling (default `[403]`).
   * Pass a broader set when other statuses are terminal for the resource —
   * e.g. `[403, 404]` when a forbidden or nonexistent audit should not be
   * retried every tick.
   */
  stopOnStatuses?: number[];
}

export interface UsePollingResourceResult<T> {
  data: T | null;
  loading: boolean;
  isPolling: boolean;
  errorCode: string | undefined;
  errorMessage: string | null;
  refresh: () => void;
}

export function usePollingResource<T>(
  fetchFn: () => Promise<T>,
  key: string | undefined,
  opts: UsePollingResourceOptions<T>,
): UsePollingResourceResult<T> {
  const { intervalMs, maxDurationMs } = opts;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(true);
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const startedAtRef = useRef(0);
  const fetchGenerationRef = useRef(0);
  // Generations of fetches currently in flight. The poll-interval tick skips
  // itself when a fetch of the CURRENT generation is still running: otherwise
  // overlapping same-generation responses resolve out of order and the last
  // one wins (a slow `processing` could stick after a fast `completed`
  // already stopped polling). Key-change and refresh() intentionally bypass
  // the guard — their generation bump / restart is authoritative.
  const inFlightGenerationsRef = useRef(new Set<number>());

  const fetchFnRef = useRef(fetchFn);
  const isTerminalRef = useRef(opts.isTerminal);
  const stopOnStatusesRef = useRef<number[] | undefined>(opts.stopOnStatuses);
  // Sync the refs in an effect (not during render) — declared first so it
  // runs before the reset/interval effects below in the same commit,
  // keeping fetchFnRef.current, isTerminalRef.current, and
  // stopOnStatusesRef.current up to date whenever those effects call
  // fetchOnce.
  useEffect(() => {
    fetchFnRef.current = fetchFn;
    isTerminalRef.current = opts.isTerminal;
    stopOnStatusesRef.current = opts.stopOnStatuses;
  });

  // Core fetch. Tracks the generation in `inFlightGenerationsRef` so the
  // interval tick knows whether its own generation is still running. A
  // generation bump (key change) is authoritative: the stale fetch keeps
  // running, its stale response is discarded by the generation check, and the
  // new generation is free to start its own.
  const fetchCore = useCallback(async () => {
    if (!key) return;
    const fetchId = fetchGenerationRef.current;
    inFlightGenerationsRef.current.add(fetchId);
    try {
      const result = await fetchFnRef.current();
      if (fetchId !== fetchGenerationRef.current) return;
      setData(result);
      setErrorCode(undefined);
      setErrorMessage(null);
      if (isTerminalRef.current?.(result)) {
        setIsPolling(false);
      }
    } catch (err) {
      if (fetchId !== fetchGenerationRef.current) return;
      if (err instanceof ApiError) {
        setErrorCode(err.code);
        setErrorMessage(err.message);
        const stopOnStatuses = stopOnStatusesRef.current ?? [403];
        if (stopOnStatuses.includes(err.status)) {
          setIsPolling(false);
        }
      } else {
        setErrorMessage(err instanceof Error ? err.message : String(err));
      }
    } finally {
      inFlightGenerationsRef.current.delete(fetchId);
      if (fetchId === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [key]);

  // Poll-interval entry point: skips the tick when this generation already
  // has a fetch in flight, so a slow response can never overlap a newer one
  // of the same key (last-to-resolve would otherwise win and clobber
  // fresher state, e.g. a stuck `processing` after `completed`).
  const fetchOnce = useCallback(async () => {
    const current = fetchGenerationRef.current;
    if (inFlightGenerationsRef.current.has(current)) return;
    void fetchCore();
  }, [fetchCore]);

  const refresh = useCallback(() => {
    startedAtRef.current = Date.now();
    setIsPolling(true);
    setLoading(true);
    // Refresh is authoritative: bump the generation so any in-flight fetch of
    // the previous generation is invalidated (its response is discarded by
    // the generation check in fetchCore), then start the new fetch directly
    // instead of going through fetchOnce, which would skip the tick when a
    // same-generation fetch is already running.
    fetchGenerationRef.current += 1;
    void fetchCore();
  }, [fetchCore]);

  useEffect(() => {
    fetchGenerationRef.current += 1;
    setData(null);
    setErrorCode(undefined);
    setErrorMessage(null);

    if (!key) {
      setLoading(false);
      setIsPolling(false);
      return;
    }

    startedAtRef.current = Date.now();
    setLoading(true);
    setIsPolling(true);
    void fetchOnce();
  }, [key, fetchOnce]);

  useEffect(() => {
    if (!key || !isPolling) {
      return;
    }

    const interval = window.setInterval(() => {
      if (Date.now() - startedAtRef.current > maxDurationMs) {
        setIsPolling(false);
        return;
      }
      void fetchOnce();
    }, intervalMs);

    return () => window.clearInterval(interval);
  }, [key, isPolling, fetchOnce, intervalMs, maxDurationMs]);

  return { data, loading, isPolling, errorCode, errorMessage, refresh };
}
